import { useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { useExpirationStatus } from "@/controllers/hooks/useExpirationStatus";

const SUPPORT_EMAIL =
  import.meta.env.VITE_SUPPORT_EMAIL ?? "contato@engenhariadosim.com.br";

const DISMISS_KEY = "expiration_banner_dismissed_until";

function isDismissed(): boolean {
  const stored = localStorage.getItem(DISMISS_KEY);
  if (!stored) return false;
  return new Date(stored) > new Date();
}

function dismissUntilTomorrow() {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  localStorage.setItem(DISMISS_KEY, tomorrow.toISOString());
}

export function ExpirationWarningBanner() {
  const { data: status } = useExpirationStatus();
  const [dismissed, setDismissed] = useState(isDismissed());

  if (!status || status.isLifetime) return null;
  if (status.daysRemaining === null) return null;
  if (status.daysRemaining > 30 || status.daysRemaining < 0) return null;

  const isUrgent = status.daysRemaining <= 7;
  const dismissable = !isUrgent;

  if (dismissed && dismissable) return null;

  const palette = isUrgent
    ? "bg-red-50 border-red-200 text-red-900"
    : "bg-amber-50 border-amber-200 text-amber-900";

  const text =
    status.daysRemaining === 0
      ? "Hoje é o último dia de acesso."
      : isUrgent
        ? `⚠️ Seu acesso expira em ${status.daysRemaining} dia${status.daysRemaining === 1 ? "" : "s"}. Renove agora para não perder acesso.`
        : `Seu acesso expira em ${status.daysRemaining} dias (${status.expiresAt?.toLocaleDateString(
            "pt-BR",
          )}). Fale com a equipe para renovar.`;

  return (
    <div
      className={`w-full border-b ${palette} px-4 py-2.5 flex items-center justify-between gap-3`}
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span>{text}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <a
          href={`mailto:${SUPPORT_EMAIL}?subject=Renovação de acesso`}
          className="text-sm font-semibold underline hover:no-underline"
        >
          Falar com a equipe
        </a>
        {dismissable && (
          <button
            type="button"
            onClick={() => {
              dismissUntilTomorrow();
              setDismissed(true);
            }}
            className="p-1 rounded hover:bg-black/5"
            aria-label="Dispensar"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
